/**
 * Krok výběru platební metody — 100 % předem online (od 18. 8. 2026 u všeho,
 * ubytování i sport; „platba na místě" zrušena).
 *
 * Stav brány se načítá ze serveru (`GET /api/sobacov/payments/status`):
 *   live=false → metody se ukážou jako „připravujeme" (QR přeškrtnuté), host
 *                dostane info, že rezervaci potvrdíme telefonicky.
 *   live=true  → klik zavolá /payments/init a přesměruje na bránu.
 * Po vložení GoPay klíčů do systemd env se tedy zapne SAMO, bez rebuildu webu.
 */
import { useEffect, useState } from 'react'
import { apiUrl } from '../lib/api'

type Props = {
  priceCzk: number
  variableSymbol?: string
  reservationId?: number | null
  onContinue: () => void
}

export function PaymentMethodStep({ priceCzk, variableSymbol, reservationId, onContinue }: Props) {
  const [showInfo, setShowInfo] = useState<string | null>(null)
  const [live, setLive] = useState<boolean | null>(null)   // null = zjišťujeme
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(apiUrl('/api/sobacov/payments/status'))
      .then((r) => (r.ok ? r.json() : { live: false }))
      .then((j) => { if (!cancelled) setLive(!!j.live) })
      .catch(() => { if (!cancelled) setLive(false) })
    return () => { cancelled = true }
  }, [])

  const PAYMENT_LIVE = live === true

  async function click(method: string) {
    if (!PAYMENT_LIVE) {
      setShowInfo(method)
      return
    }
    if (!reservationId) {
      setErr('Chybí číslo rezervace — zavolejte prosím recepci.')
      return
    }
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch(apiUrl('/api/sobacov/payments/init'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservation_id: reservationId, payment_type: 'full' }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.detail || 'Platbu se nepodařilo založit')
      if (j.gw_url) window.location.href = j.gw_url
      else throw new Error('Brána nevrátila platební odkaz')
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Cena + VS */}
      <div className="bg-gradient-to-br from-steel-50 via-white to-brand-50/30 border border-steel-200 p-4 text-center">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-steel-700">K úhradě</div>
        <div className="text-4xl font-bold text-ink-900 tabular-nums mt-1">
          {priceCzk.toLocaleString('cs')} <span className="text-2xl text-ink-700">Kč</span>
        </div>
        {variableSymbol && (
          <div className="mt-2 text-[11px] text-ink-700">
            VS: <span className="font-mono font-bold text-ink-900">{variableSymbol}</span>
          </div>
        )}
      </div>

      {/* Hlavní metoda — QR */}
      <div className="border border-ink-900/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-brand-600 mb-3 flex items-center gap-2">
          <span className="inline-block w-6 h-px bg-brand-500" />
          Možnost 1 · Naskenuj QR
        </div>
        <div className="flex flex-col items-center">
          <div className="relative">
            <FakeQR size={200} />
            {!PAYMENT_LIVE && (
              <>
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  <div className="w-full h-1 bg-brand-500 rotate-[-25deg] origin-center shadow-[0_0_10px_rgba(229,2,69,0.6)]" />
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-[9px] uppercase tracking-[0.20em] font-bold px-2 py-1 whitespace-nowrap">
                  Neaktivní · příprava
                </div>
              </>
            )}
          </div>
          <p className="mt-5 text-[12px] text-ink-700 text-center max-w-xs">
            {PAYMENT_LIVE
              ? 'Naskenujte QR kód bankovní aplikací — platba se spáruje automaticky.'
              : 'QR platba bude aktivní po spuštění platební brány.'}
          </p>
        </div>
      </div>

      {/* Ostatní metody */}
      <div className="border border-ink-900/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-brand-600 mb-3 flex items-center gap-2">
          <span className="inline-block w-6 h-px bg-brand-500" />
          Možnost 2 · Karta nebo převod
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'card', label: 'Platební karta' },
            { id: 'apple', label: 'Apple Pay' },
            { id: 'google', label: 'Google Pay' },
            { id: 'transfer', label: 'Bankovní převod' },
          ].map((m) => (
            <button
              key={m.id}
              disabled={busy}
              onClick={() => click(m.id)}
              className="border border-ink-900/15 hover:border-brand-500 hover:bg-brand-50 px-3 py-3 text-sm font-semibold text-ink-900 transition disabled:opacity-50"
            >
              {m.label}
            </button>
          ))}
        </div>
        {busy && <div className="mt-3 text-xs text-ink-700">Přesměrovávám na platební bránu…</div>}
        {err && <div className="mt-3 text-xs text-brand-600 font-semibold">{err}</div>}
      </div>

      {!PAYMENT_LIVE && live !== null && (
        <div className="bg-brand-50 border border-brand-200 px-4 py-3 text-[13px] text-ink-800">
          <b className="text-brand-600">Platební brána se právě spouští.</b> Vaši rezervaci
          máme uloženou — ozveme se telefonicky a domluvíme úhradu.
        </div>
      )}

      <button onClick={onContinue} className="btn-outline-dark w-full text-sm">
        Hotovo →
      </button>

      {showInfo && (
        <div
          className="fixed inset-0 z-[110] bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setShowInfo(null)}
        >
          <div className="bg-white max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-brand-600">
              Připravujeme
            </div>
            <h4 className="font-bold text-lg text-ink-900 mt-2">Online platba se spouští</h4>
            <p className="mt-2 text-sm text-ink-700 leading-relaxed">
              Rezervaci máme uloženou včetně termínu. Ozveme se vám telefonicky
              a domluvíme úhradu — jakmile bude brána v provozu, zaplatíte rovnou tady.
            </p>
            <button onClick={() => setShowInfo(null)} className="btn-book w-full mt-5">
              Rozumím
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function FakeQR({ size = 200 }: { size?: number }) {
  const cells = 21
  const cell = size / cells
  // Deterministický vzor — jen dekorace, nikdy se neskenuje jako platná platba.
  const filled: [number, number][] = []
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      if ((r * 7 + c * 5 + ((r * c) % 3)) % 3 === 0) filled.push([r, c])
    }
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="bg-white">
      <rect width={size} height={size} fill="#fff" />
      {filled.map(([r, c], i) => (
        <rect key={i} x={c * cell} y={r * cell} width={cell} height={cell} fill="#0D1A13" />
      ))}
      {[[0, 0], [0, cells - 7], [cells - 7, 0]].map(([r, c], i) => (
        <g key={`f${i}`}>
          <rect x={c * cell} y={r * cell} width={cell * 7} height={cell * 7} fill="#fff" />
          <rect x={c * cell} y={r * cell} width={cell * 7} height={cell * 7} fill="none" stroke="#0D1A13" strokeWidth={cell} />
          <rect x={(c + 2) * cell} y={(r + 2) * cell} width={cell * 3} height={cell * 3} fill="#0D1A13" />
        </g>
      ))}
    </svg>
  )
}
